import type { RatingHistoryPoint } from './types';

/**
 * Utilitaires purs pour `<EloHistoryChart>` (T5 P1 Stats Hub).
 *
 * Extraits dans un fichier dédié pour respecter
 * `react-refresh/only-export-components` (composant TSX ↔ utils TS).
 */

/**
 * Downsample uniforme si la série dépasse `cap` points (par défaut 365).
 * Garantit que le dernier point est conservé (pour ne pas tronquer la
 * progression la plus récente).
 *
 * Voir ROADMAP §risque 4 : 1 point/jour cap à 1 an de Lichess = ~365 max.
 */
export function downsamplePoints(
  points: ReadonlyArray<RatingHistoryPoint>,
  cap = 365,
): RatingHistoryPoint[] {
  if (points.length <= cap) return [...points];
  const step = Math.ceil(points.length / cap);
  const out: RatingHistoryPoint[] = [];
  for (let i = 0; i < points.length; i += step) {
    const p = points[i];
    if (p) out.push(p);
  }
  const last = points[points.length - 1];
  if (last && out[out.length - 1] !== last) out.push(last);
  return out;
}

/** Convertit "YYYY-MM-DD" en timestamp ms (UTC midnight pour cohérence cross-TZ). */
export function tsOf(dateISO: string): number {
  return Date.parse(dateISO + 'T00:00:00Z');
}

/** Calcule la date `since` ISO (YYYY-MM-DD) depuis aujourd'hui — null = pas de filtre. */
export function sinceFromDays(days: number | null): string | undefined {
  if (days === null) return undefined;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/** Path SVG "M..L.." ou Bézier cubique simple si `smooth`. */
export function buildPath(
  points: ReadonlyArray<{ x: number; y: number }>,
  smooth: boolean,
): string {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const p = points[0]!;
    return `M${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
  }
  if (!smooth) {
    return points
      .map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
      .join(' ');
  }
  const segs: string[] = [];
  const first = points[0]!;
  segs.push(`M${first.x.toFixed(1)} ${first.y.toFixed(1)}`);
  for (let i = 1; i < points.length; i++) {
    const p0 = points[i - 1]!;
    const p1 = points[i]!;
    const cx = (p0.x + p1.x) / 2;
    segs.push(
      `C${cx.toFixed(1)} ${p0.y.toFixed(1)} ${cx.toFixed(1)} ${p1.y.toFixed(1)} ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`,
    );
  }
  return segs.join(' ');
}
